import type { IModelConnection } from "@itwin/core-frontend";
import { QueryBinder, QueryRowFormat } from "@itwin/core-common";
import { UtilityData } from "../../../domain/utilities";
import { resolveEnumDisplayValue } from "./enumDisplayResolver";

/**
 * ECSQL to read Utility Data for an electrical node element.
 *
 * Combines:
 *   - BaseNode_NetworkDataAspect        (generic node data)
 *   - ElectricalNode_NetworkDataAspect  (electric-specific network/node type)
 */
const ECSQL_UTILITY_DATA_BY_ELEMENT = `
  SELECT
    b.NetworkData_UserDefinedElementType AS ElementTypeUserDefined,
    e.ElectricNetworkType                AS NetworkType,
    b.NetworkData_UserDefinedNetworkType AS NetworkTypeUserDefined,
    e.ElectricNodeDataType               AS NodeType,
    b.UtiltityOperationalStatus          AS OperationalStatus,
    b.NetworkData_UtilityOwner           AS Owner
  FROM CifSubsurfaceConflictAnalysis.BaseNode_NetworkDataAspect b
  LEFT JOIN CifSubsurfaceConflictAnalysis.ElectricalNode_NetworkDataAspect e
         ON e.Element.Id = b.Element.Id
  WHERE b.Element.Id = ?
`;

/**
 * Query Utility Data for a single element.
 */
export async function queryUtilityDataForElement(
  iModel: IModelConnection,
  elementId: string,
): Promise<UtilityData | undefined> {
  const reader = iModel.createQueryReader(
    ECSQL_UTILITY_DATA_BY_ELEMENT,
    QueryBinder.from([elementId]),
    { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
  );

  for await (const row of reader) {
    const plain = row.toRow() as any;
    const decoded: any = { ...plain };
    decoded.NetworkType = await resolveEnumDisplayValue(
      iModel,
      "CifSubsurfaceConflictAnalysis.ElectricalNode_NetworkDataAspect",
      "ElectricNetworkType",
      plain.NetworkType
    );
    decoded.NodeType = await resolveEnumDisplayValue(
      iModel,
      "CifSubsurfaceConflictAnalysis.ElectricalNode_NetworkDataAspect",
      "ElectricNodeDataType",
      plain.NodeType
    );
    decoded.OperationalStatus = await resolveEnumDisplayValue(
      iModel,
      "CifSubsurfaceConflictAnalysis.BaseNode_NetworkDataAspect",
      "UtiltityOperationalStatus",
      plain.OperationalStatus
    );
    return UtilityData.fromObject(decoded);
  }

  // No network data for this element
  return undefined;
}
